<script setup>
import { computed, onMounted, ref } from 'vue'
import { useStore } from 'vuex'

import { useSpeckleStore } from '@haskoning/vue-speckle'

import DikeSectionIndex from '@/components/dikeSection/DikeSectionIndex.vue'
import DikeTrajectoryDrawerTitle from '@/components/dike/DikeTrajectoryDrawerTitle.vue'
import DikeTrajectoryUploadStepper from '@/components/dike/DikeTrajectoryUploadStepper.vue'
import SchematisationTable from '@/components/schematisation/SchematisationTable.vue'
import GenerateElevationProfilesLoadingBar from '@/components/dike/GenerateElevationProfilesLoadingBar.vue'
import { useRoute } from 'vue-router'
import { useProjectSpeckleStore } from '@/store/projectSpeckle'
import { storeToRefs } from 'pinia'

const store = useStore()
const speckleStore = useSpeckleStore()
const route = useRoute()
const projectSpeckleStore = useProjectSpeckleStore()
const { currentSelection } = storeToRefs(speckleStore)

const drawerConfig = ref({
  maxWidth: 1000,
  minWidth: 350,
  borderSize: 6,
  hideItemsThreshold: 700
})

const speckleLoading = ref(false)

const currentProject = computed(() => store.state.project.currentProject)
const loading = computed(() => store.state.project.loading)
const edit = computed(() => store.state.project.edit)
const dikeTrajectDrawerWidth = computed(() => store.state.project.dikeTrajectDrawerWidth)
const referencePointLoaded = computed(() => store.state.referencePoint.loaded)
const items = computed(() => store.state.stabSchematisation.items)
const showList = computed(() => store.state.stabSchematisation.showList)
const dikeProfilesLoaded = computed(() => store.state.dikeProfiles.loaded)
const dikeSectionLoaded = computed(() => store.state.dikeSection.loaded)

const dikeSectionLoading = computed(() => store.state.dikeSection.loading)
const stabSchematisationLoading = computed(() => store.state.stabSchematisation.loading)

const dikeTrajectImported = computed(() => {
  return dikeProfilesLoaded.value && referencePointLoaded.value && dikeSectionLoaded.value
})

const hideUnimportantItems = computed(() => {
  return dikeTrajectDrawerWidth.value < drawerConfig.value.hideItemsThreshold
})

onMounted(async () => {
  speckleLoading.value = true
  try {
    await speckleStore.getAllUserProjects()
    await projectSpeckleStore.fetchProjectSpeckle(route.params.id)
    await speckleStore.getSpeckleModels()
    const projectId = currentSelection.value.project?.id
    if (projectId) {
      await projectSpeckleStore.addDefaultModel(projectId)
    }
    await speckleStore.getSpeckleVersions()
  } finally {
    speckleLoading.value = false
  }
})
</script>
<template>
  <v-navigation-drawer
    ref="drawer"
    height="30%"
    :width="dikeTrajectDrawerWidth"
    :model-value="true"
    location="right"
  >
    <div v-if="!loading" class="fill-height">
      <div v-if="dikeTrajectImported && !edit" style="height: 80%">
        <div class="sticky">
          <DikeTrajectoryDrawerTitle :showEdit="true" />
          <v-divider></v-divider>
        </div>
        <GenerateElevationProfilesLoadingBar v-if="currentProject.uploading_dike_profiles" />

        <div class="fill-height">
          <div v-show="showList">
            <v-list density="compact" class="pt-0">
              <div v-if="!items.length && !stabSchematisationLoading" class="pa-3 pt-1">
                <small>
                  No schematisations available. To create a schematisation, navigate to the dike
                  profile you would like to create a schematisation for.
                </small>
              </div>

              <template v-if="stabSchematisationLoading">
                <v-skeleton-loader v-for="n in 0" :key="n" type="list-item-two-line" class="ma-2" />
              </template>

              <SchematisationTable
                v-else-if="items.length"
                :hide-unimportant-items="hideUnimportantItems"
                :items="items"
              />
            </v-list>
          </div>

          <div v-show="!showList">
            <template v-if="dikeSectionLoading">
              <v-skeleton-loader
                v-for="n in 0"
                :key="n"
                type="list-item-avatar-three-line"
                class="ma-2"
              />
            </template>

            <DikeSectionIndex
              v-else-if="dikeSectionLoaded"
              :hide-unimportant-items="hideUnimportantItems"
            />
          </div>
        </div>
      </div>
      <div v-else class="fill-height">
        <DikeTrajectoryUploadStepper />
      </div>
    </div>
  </v-navigation-drawer>
</template>
<style scoped>
.v-list {
  padding-top: 0;
  padding-bottom: 0;
}

.sticky {
  position: sticky;
  top: 0;
  z-index: 300;
  background-color: rgb(255, 255, 255);
}
</style>
